
// RichContentEditor.jsx
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Editor
  , EditorState
  , convertFromRaw
  , convertToRaw
  , RichUtils
  , CompositeDecorator
  , Modifier
} from "draft-js";
import "draft-js/dist/Draft.css";
import StyledButton from 'shared-resources/components/src/StyledButton';

// Link component to render link entities
const Link = (props) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a href={url} style={{ textDecoration: "underline" }}>
      {props.children}
    </a>
  );
};

// Strategy to find link entities in content
function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === "LINK"
      );
    },
    callback
  );
}

// Decorator to apply the link component
const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

const RichContentEditor = ({
  value
  , name
  , onChange = () => console.warn('onChange not defined')
  , placeholder = "Enter text here..."
  , autoFocus = false
  , className = ''
  , controlsStyle = {}
}) => {
  const editorRef = useRef(null);
  function focusEditor() {
    editorRef.current.focus();
  }

  // Initialize editor state with decorator
  const [editorState, setEditorState] = useState(() => {
    if(value) {
      const newState = { ...value };
      if(!newState.entityMap) newState.entityMap = {};
      const contentState = convertFromRaw(newState);
      return EditorState.createWithContent(contentState, decorator);
    } else {
      return EditorState.createEmpty(decorator);
    }
  });

  function handleChange(newEditorState) {
    setEditorState(newEditorState);
    const newRawContent = convertToRaw(
      newEditorState?.getCurrentContent()
    );
    onChange({
      target: {
        name: name
        , value: newRawContent
      },
    });
  }

  // Function to add a link to selected text
  function onAddLink() {
    const selection = editorState.getSelection();
    if(!selection.isCollapsed()) {
      const url = window.prompt("Enter the URL", "https://");
      if(!url) {
        return;
      }
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", { url });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      let newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      newEditorState = RichUtils.toggleLink(newEditorState, selection, entityKey);
      handleChange(newEditorState);
    } else {
      alert("Please select some text to link.");
    }
  }

  // Function to remove a link from selected text
  function onRemoveLink() {
    const selection = editorState.getSelection();
    if(!selection.isCollapsed()) {
      const newEditorState = RichUtils.toggleLink(editorState, selection, null);
      handleChange(newEditorState);
    }
  }

  function toggleBlockType(blockType) {
    handleChange(RichUtils.toggleBlockType(editorState, blockType));
  }

  function toggleInlineStyle(inlineStyle) {
    handleChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  }

  // Handle key commands like Ctrl+B for bold
  function handleKeyCommand(command) {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if(newState) {
      handleChange(newState);
    } else {
      return
    }
  }

  function toggleAlignment(alignment) {
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const blockData = contentState.getBlockForKey(blockKey).getData();
    const newBlockData = blockData.merge({ 'text-align': alignment });
    const newContentState = Modifier.setBlockData(
      contentState,
      selection,
      newBlockData
    );

    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'change-block-data'
    );

    handleChange(newEditorState);
  }

  useEffect(() => {
    if(autoFocus) focusEditor();
  }, [autoFocus]);

  return (
    <div
      className={`border-2 rounded-lg p-2 bg-neutral-pri h-fit min-w-fit max-w-full ${className}`}
      onClick={focusEditor}
    >
      <div className="flex flex-row gap-2 py-2 flex-wrap sm:flex-nowrap sticky top-0 z-50 bg-neutral-pri" style={{ ...controlsStyle }}>
        <BlockStyleControls
          editorState={editorState}
          onToggle={toggleBlockType}
        />
        {/* Divider */}
        <span className="p-[.5px] my-2 min-h-full bg-neutral-ter hidden sm:block" />
        <InlineStyleControls
          editorState={editorState}
          onToggle={toggleInlineStyle}
        />
        {/* Divider */}
        <span className="p-[.5px] my-2 min-h-full bg-neutral-ter hidden sm:block" />
        {/* Alignment Controls */}
        <AlignmentControls
          editorState={editorState}
          onToggle={toggleAlignment}
        />
        {/* Divider */}
        <span className="p-[.5px] my-2 min-h-full bg-neutral-ter hidden sm:block" />
        <LinkControls
          editorState={editorState}
          onAddLink={onAddLink}
          onRemoveLink={onRemoveLink}
        />
      </div>
      <div className="min-h-[5rem]">
        <Editor
          ref={editorRef}
          customStyleMap={inlineStyleMap}
          editorState={editorState}
          onChange={handleChange}
          name={name}
          placeholder={placeholder}
          handleKeyCommand={handleKeyCommand}
          blockStyleFn={blockStyleFn}
        />
      </div>
    </div>
  );
};

RichContentEditor.propTypes = {
  value: PropTypes.object
  , name: PropTypes.string
  , onChange: PropTypes.func
  , placeholder: PropTypes.string
  , autoFocus: PropTypes.bool
  , className: PropTypes.string
  , controlsStyle: PropTypes.object
};

export default RichContentEditor;

const BLOCK_TYPES = [
  { label: 'H1', style: 'header-one' },
  { label: 'H2', style: 'header-two' },
  { label: 'H3', style: 'header-three' },
  { label: 'P', style: 'unstyled' },
  { label: <i key={'UL'} className="fa fa-list-ul" />, style: 'unordered-list-item' },
  { label: <i key={'OL'} className="fa fa-list-ol" />, style: 'ordered-list-item' },
];

const BlockStyleControls = ({ editorState, onToggle }) => {
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  return (
    <div className="flex flex-row gap-2 p-">
      {BLOCK_TYPES.map((type, i) => (
        <EditorButton
          key={i + type.style}
          active={type.style === blockType}
          label={type.label}
          onToggle={onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};

const INLINE_STYLES = [
  { label: <strong>B</strong>, style: 'BOLD' },
  { label: <em>I</em>, style: 'ITALIC' },
  { label: <u>U</u>, style: 'UNDERLINE' },
  { label: <sup>sup</sup>, style: 'SUPERSCRIPT' },
  { label: <sub>sub</sub>, style: 'SUBSCRIPT' },
];

// in order to display these correctly in the editor (bold underline and italic work out of the box), https://draftjs.org/docs/advanced-topics-inline-styles#mapping-a-style-string-to-css
const inlineStyleMap = {
  SUPERSCRIPT: {
    verticalAlign: 'super',
    fontSize: 'smaller',
  },
  SUBSCRIPT: {
    verticalAlign: 'sub',
    fontSize: 'smaller',
  },
  BOLD: {
    fontWeight: 600
  },
};

const InlineStyleControls = ({ editorState, onToggle }) => {
  const currentStyle = editorState.getCurrentInlineStyle();
  return (
    <div className="flex flex-row gap-2">
      {INLINE_STYLES.map((type, i) => (
        <EditorButton
          key={i + type.style}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};

const ALIGNMENT_TYPES = [
  { label: <i className="fa fa-align-left" />, style: 'left' },
  { label: <i className="fa fa-align-center" />, style: 'center' },
  { label: <i className="fa fa-align-right" />, style: 'right' },
  { label: <i className="fa fa-align-justify" />, style: 'justify' },
];

const AlignmentControls = ({ editorState, onToggle }) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const blockKey = selection.getStartKey();
  const blockData = contentState.getBlockForKey(blockKey).getData();
  const currentAlignment = blockData.get('text-align') || 'left';

  return (
    <div className="flex flex-row gap-2">
      {ALIGNMENT_TYPES.map((type, i) => (
        <EditorButton
          key={i}
          active={currentAlignment === type.style}
          label={type.label}
          onToggle={() => onToggle(type.style)}
          style={type.style}
        />
      ))}
    </div>
  );
};

// Function to apply custom styles to blocks based on their data
// This is only for how it displays while editing
function blockStyleFn(contentBlock) {
  // in order for tailwind to include the class in the initial bundle, we have to use the whole class name instead of a variable
  // alternatively we could add these special rich text editor classes to the `safeList` in tailwind.config.js but that's unexplored territory
  // check index.css for the info on the classes below
  const blockType = contentBlock.getType(); // e.g., 'header-one'
  const textAlign = contentBlock.getData().get('text-align') || 'left';

  let styleClass = '';

  // Add custom header class
  switch (blockType) {
    case 'header-one':
      styleClass += 'font-sans text-4xl font-semibold ';
      break;
    case 'header-two':
      styleClass += 'font-sans text-3xl font-semibold ';
      break;
    case 'header-three':
      styleClass += 'font-sans text-2xl font-semibold ';
      break;
    default:
      break;
  }

  // Add alignment class
  switch (textAlign) {
    case 'center':
      styleClass += 'text-align-center ';
      break;
    case 'right':
      styleClass += 'text-align-right ';
      break;
    case 'justify':
      styleClass += 'text-align-justify ';
      break;
    default:
      styleClass += 'text-align-left ';
  }

  return styleClass.trim();
}

const LinkControls = ({ editorState, onAddLink, onRemoveLink }) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(selection.getStartKey());
  const entityKey = block.getEntityAt(selection.getStartOffset());
  let hasLink = false;
  if(entityKey) {
    const entity = contentState.getEntity(entityKey);
    hasLink = entity.getType() === "LINK";
  }

  const toggleLink = () => {
    if(hasLink) {
      onRemoveLink();
    } else {
      onAddLink();
    }
  };

  return (
    <div className="flex flex-row p-0 sm:p-">
      <EditorButton
        active={hasLink}
        label={<i className={`fa ${hasLink ? "fa-unlink" : "fa-link"}`} />}
        onToggle={toggleLink}
      />
    </div>
  );
};

const EditorButton = ({ onToggle, style, active, label }) => {

  const variant = active ? 'primary' : 'muted';

  return (
    <StyledButton
      variant={variant}
      size='xs'
      onMouseDown={(e) => {
        e.preventDefault();
        onToggle(style);
      }}
      className={'rounded-full border-none'}
    >
      {label}
    </StyledButton>
  )
};
